import { ButtonWithSpinner } from 'components/Button';
import React from 'react';
import styled from 'styled-components';
import { AddMultipleToBasket } from 'autogen/swagger/all';
import mediator from 'modules/mediator';
import track from 'modules/tracking/dataLayer';
import { Spacing8, Spacing24 } from 'autogen/design-tokens/tokens';
import { useTranslation } from 'react-i18next';
import * as transKey from 'autogen/translation-keys/trans-root-website';
import { breakpoint, sizes } from 'variables';
import UAEventTracking from 'modules/tracking/UAEventTracking';
import PriceModule from 'components/generic/productPrice/PriceModule';
import { Body } from 'components/generic/typography';
import { refreshBasketQuantityGlobally } from 'hooks/basket/useBasketQuantity';
import { GA4Tracking } from 'modules/tracking/GA4/GA4Tracking';
import { useRegime } from './Regime';

async function subsequentlyAddToBasket(productIds): Promise<void> {
    const { subsequentlyAddToBasket: subsubsequentlyAddToBasketMethod } = await import(
        /* webpackChunkName: "addAndRemoveFromBasket" */ 'modules/addAndRemoveFromBasket'
    );

    subsubsequentlyAddToBasketMethod(productIds);
}

/* #region  styles */
const StyledButton = styled(ButtonWithSpinner)`
    justify-self: center;
    width: 260px;
`;

const Container = styled.div`
    margin-top: ${Spacing24};
    display: grid;
    justify-content: center;
    grid-column: 1;
    gap: ${Spacing8};

    ${breakpoint.up(sizes.sm)} {
        justify-content: center;
    }
`;

const StyledPriceModule = styled(PriceModule)`
    && {
        text-align: center;
    }
`;
/* #endregion */

export default function RegimePriceButton({ ...restProps }) {
    const { regimeModel, getSelectedProducts, hasSelectedProducts, mainProductRegimeId } = useRegime();
    const { earnXPointsText, addXToBasketText, priceBeforeText, priceText, onDiscount } = regimeModel;
    const { t } = useTranslation();

    const hasProductsSelected = hasSelectedProducts();

    const addToBasket = async () => {
        const selectedProducts = getSelectedProducts();

        const params: any = {
            productIds: selectedProducts,
            bundleRelation: mainProductRegimeId.toString(),
            bundleType: 'Regime'
        };

        await AddMultipleToBasket(params).then((response) => {
            refreshBasketQuantityGlobally();
            mediator.publish('updateMiniBasketView');
            subsequentlyAddToBasket(selectedProducts);
            track(response.dataLayerDto);
            UAEventTracking({
                eventCategory: 'productRegime',
                eventAction: 'addToBasket',
                eventLabel: 'multiple'
            });

            GA4Tracking({
                eventName: 'add_to_cart',
                context: 'product_page',
                category: 'ecommerce',
                eventParams: [
                    {
                        param_name: 'is_regime',
                        param_value: true
                    },
                    {
                        param_name: 'add_to_cart_quantity',
                        param_value: selectedProducts.length
                    }
                ]
            });
        });
    };

    return (
        <Container {...restProps}>
            {priceText ? (
                <StyledPriceModule
                    price={priceText}
                    priceLabel={t(transKey.ProductRegime.PriceTotal) as any}
                    priceBefore={priceBeforeText}
                    onDiscount={onDiscount}
                    size="sm"
                />
            ) : null}

            <StyledButton disabled={!hasProductsSelected} onClick={addToBasket}>
                {hasProductsSelected ? addXToBasketText : t(transKey.ProductRegime.NoProductsSelected)}
            </StyledButton>

            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation> */}
            <Body size="M" dangerouslySetInnerHTML={{ __html: earnXPointsText }} />
        </Container>
    );
}
